import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1721937718756-3bfec49f42a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function LoginView() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("pt@epgroup.co.uk");
    setPassword("Myton2027!");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      {/* Brand panel */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="ASDA distribution centre and logistics facility" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/55 to-slate-950/35" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <img src="/asda-logo.png" alt="ASDA" className="h-12 w-auto self-start" />
          <div>
            <h1 className="font-display font-bold text-4xl leading-tight mb-4 [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
              Supply Chain Reconciliation Hub
            </h1>
            <p className="text-slate-200 max-w-md [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]">
              Maintain DC rate cards, track IPL supplier spend, bulk-upload updates with duplicate checking, and browse
              the 4-4-5 financial calendar through FY31.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <span>Powered by</span>
            <img src="/ep-logo.png" alt="EP Group" className="h-6 w-auto opacity-90" />
          </div>
        </div>
      </div>

      {/* Credentials */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white dark:bg-slate-950">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/asda-logo.png" alt="ASDA" className="h-7 w-auto" />
            <span className="font-display font-bold text-xl text-slate-900 dark:text-white">Supply Chain Hub</span>
          </div>
          <h2 className="font-display font-bold text-2xl text-slate-900 dark:text-white mb-6">Welcome back</h2>

          {error && (
            <div className="flex items-start gap-2 mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@epgroup.co.uk"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full bg-[#78BE20] hover:bg-[#5fa016]">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <button
            type="button"
            onClick={fillDemo}
            data-testid="login-demo-credentials-button"
            className="mt-5 w-full text-center text-sm text-[#78BE20] hover:text-[#5fa016] hover:underline"
          >
            Use demo credentials
          </button>
        </div>
      </div>
    </div>
  );
}
